import { updateProjectPrefs } from "@/api";
import {
  getModeForPrimaryTool,
  getSubtoolsForMode,
} from "@/data/tool-mode-map";

export type TrackedPrefs = {
  theme?: string;
  currentLanguage?: string;
  currentBreakpoint?: string;
  stagePosition?: { x: number; y: number } | null;
  zoomMode?: string;
  zoomValue?: number;
  helpers?: {
    rulers?: boolean;
    snap?: boolean;
    guides?: boolean;
    dots?: boolean;
    lines?: boolean;
    overlay?: boolean;
  } | null;
  audioMuted?: boolean;
  selectedMode?: string;
  selectedTool?: string;
  sketchEraserMode?: "delete" | "delete-clean";
  sketchSelectionMode?: "marquee" | "lasso";
  showAll?: {
    edit?: boolean;
    sketch?: boolean;
  } | null;
};

interface PersistorDeps {
  readonly uiState: any;
  readonly getCurrentProjectId: () => string | null;
  readonly sourceId: string;
  readonly debounceMs?: number;
}

export const createPersistor = (deps: PersistorDeps) => {
  const {
    uiState,
    getCurrentProjectId,
    sourceId,
    debounceMs = 300,
  } = deps;

  let lastPersisted: TrackedPrefs = {};

  const debounce = <T extends (...args: any[]) => void>(fn: T, ms: number) => {
    let t: any;
    return (...args: Parameters<T>) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  };

  const persistImpl = (): void => {
    const projectId = getCurrentProjectId();
    if (!projectId) return;
    try {
      const s = uiState.getState();
      const computedSelectedMode = ((): string | undefined => {
        const mode = (s as any).selectedMode;
        if (typeof mode === "string") return mode as string;
        const tool = (s as any).selected_tool;
        if (typeof tool === "string")
          return (getModeForPrimaryTool(tool) ?? undefined) as string | undefined;
        return undefined;
      })();
      const computedSelectedTool = ((): string | undefined => {
        const sub = (s as any).selectedTool ?? (s as any).selected_subtool;
        return typeof sub === "string" ? (sub as any) : undefined;
      })();
      const baseNext: TrackedPrefs = {
        theme: s.theme,
        currentLanguage: s.current_language,
        currentBreakpoint: s.current_breakpoint,
        stagePosition: s.stage_position,
        zoomMode: s.zoom_mode,
        zoomValue: s.zoom_value,
        helpers: ((): any => {
          const flags = {
            rulers: s.helpers.includes("rulers" as any),
            snap: s.helpers.includes("snap" as any),
            guides: s.helpers.includes("guides" as any),
            dots: s.helpers.includes("dots" as any),
            lines: s.helpers.includes("lines" as any),
            overlay: s.helpers.includes("overlay" as any),
          };
          return flags;
        })(),
        audioMuted: s.audio_muted,
        showAll: {
          edit: !!s.show_all?.edit,
          sketch: !!s.show_all?.sketch,
        },
      };
      const next: TrackedPrefs = {
        ...baseNext,
        ...(computedSelectedMode
          ? { selectedMode: computedSelectedMode as string }
          : {}),
        ...(computedSelectedTool
          ? { selectedTool: computedSelectedTool as string }
          : {}),
        ...(s.sketchEraserMode === "delete-clean"
          ? { sketchEraserMode: "delete-clean" }
          : s.sketchEraserMode === "delete"
          ? { sketchEraserMode: "delete" }
          : {}),
        ...(s.sketchSelectionMode === "lasso"
          ? { sketchSelectionMode: "lasso" }
          : s.sketchSelectionMode === "marquee"
          ? { sketchSelectionMode: "marquee" }
          : {}),
      };

      const body: any = {};

      if (next.theme !== lastPersisted.theme) body.theme = next.theme;
      if (next.currentLanguage !== lastPersisted.currentLanguage)
        body.currentLanguage = next.currentLanguage;
      if (next.currentBreakpoint !== lastPersisted.currentBreakpoint)
        body.currentBreakpoint = next.currentBreakpoint;

      const prevPos = lastPersisted.stagePosition;
      const currPos = next.stagePosition;
      const posChanged =
        (!!prevPos || !!currPos) &&
        (!prevPos ||
          !currPos ||
          prevPos.x !== currPos.x ||
          prevPos.y !== currPos.y);
      if (posChanged) body.stagePosition = currPos;

      if (next.zoomMode !== lastPersisted.zoomMode)
        body.zoomMode = next.zoomMode;
      if (next.zoomValue !== lastPersisted.zoomValue)
        body.zoomValue = next.zoomValue;
      if (next.audioMuted !== lastPersisted.audioMuted)
        body.audioMuted = next.audioMuted;
      if (next.sketchEraserMode !== lastPersisted.sketchEraserMode) {
        body.sketchEraserMode = next.sketchEraserMode;
      }
      if (next.sketchSelectionMode !== lastPersisted.sketchSelectionMode) {
        body.sketchSelectionMode = next.sketchSelectionMode;
      }

      // Compare helpers shallowly
      const prevHelpers = lastPersisted.helpers || {};
      const currHelpers = next.helpers || {};
      const helpersChanged =
        prevHelpers.rulers !== currHelpers.rulers ||
        prevHelpers.snap !== currHelpers.snap ||
        prevHelpers.guides !== currHelpers.guides ||
        prevHelpers.dots !== currHelpers.dots ||
        prevHelpers.lines !== currHelpers.lines ||
        prevHelpers.overlay !== currHelpers.overlay;
      if (helpersChanged) body.helpers = currHelpers;
      const prevShowAll = lastPersisted.showAll || {};
      const currShowAll = next.showAll || {};
      if (
        prevShowAll.edit !== currShowAll.edit ||
        prevShowAll.sketch !== currShowAll.sketch
      ) {
        body.showAll = {
          edit: currShowAll.edit ?? false,
          sketch: currShowAll.sketch ?? false,
        };
        body.show_all = body.showAll;
      }

      // Minimal deltas for selectedMode/selectedTool
      try {
        const prevMode = lastPersisted.selectedMode;
        const prevTool = lastPersisted.selectedTool;
        const modeChanged = next.selectedMode !== prevMode;
        const toolChanged = next.selectedTool !== prevTool;
        const mapSelectedTool = () => {
          if (typeof next.selectedTool !== "string") return false;
          const effectiveMode =
            typeof next.selectedMode === "string"
              ? next.selectedMode
              : typeof lastPersisted.selectedMode === "string"
              ? lastPersisted.selectedMode
              : (() => {
                  const uiMode = uiState?.getState?.()?.selectedMode;
                  return typeof uiMode === "string" ? uiMode : null;
                })();
          if (
            effectiveMode &&
            getSubtoolsForMode(effectiveMode as any).length === 0
          ) {
            return false;
          }
          body.selectedTool = next.selectedTool;
          body.selectedSubtool = next.selectedTool;
          body.selected_subtool = next.selectedTool;
          return true;
        };
        if (modeChanged && typeof next.selectedMode === "string") {
          body.selectedMode = next.selectedMode;
          mapSelectedTool();
        } else if (!modeChanged && toolChanged) {
          mapSelectedTool();
        }
      } catch {}

      if (Object.keys(body).length === 0) return;

      // Optimistically update snapshot to avoid duplicate sends
      lastPersisted = { ...lastPersisted, ...next };
      updateProjectPrefs(projectId, { ...body, sourceId }).catch(() => {});
    } catch {}
  };

  const persist = debounce(persistImpl, debounceMs);

  const seedSnapshot = (snapshot: TrackedPrefs) => {
    lastPersisted = { ...snapshot };
  };

  const destroy = () => {
    // nothing to clean right now
  };

  return { persist, seedSnapshot, destroy };
};
